import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import SessionContext from '../contexts/SessionContext';
import {
	addRecentSearch,
	addRecentView,
	clearRecentSearches,
	clearRecentViews,
	getRecentActivityStorageKey,
	readRecentActivity,
	RecentActivityData,
	RecentView,
} from '../utils/recentActivity';

export function useRecentActivity() {
	const session = useContext(SessionContext);
	const userId = session?.isAuthenticated ? session.userId : null;

	const storageKey = useMemo(() => getRecentActivityStorageKey(userId), [userId]);
	const [activity, setActivity] = useState<RecentActivityData>(() => readRecentActivity(userId));

	useEffect(() => {
		setActivity(readRecentActivity(userId));
	}, [storageKey, userId]);

	useEffect(() => {
		const handleStorage = (e: StorageEvent) => {
			if (e.key === storageKey) setActivity(readRecentActivity(userId));
		};
		const handleSameTab = () => setActivity(readRecentActivity(userId));
		window.addEventListener('storage', handleStorage);
		window.addEventListener('partbay:recentActivityUpdated', handleSameTab);
		return () => {
			window.removeEventListener('storage', handleStorage);
			window.removeEventListener('partbay:recentActivityUpdated', handleSameTab);
		};
	}, [storageKey, userId]);

	const trackSearch = useCallback(
		(query: string) => {
			setActivity(addRecentSearch(userId, query));
		},
		[userId]
	);

	const trackView = useCallback(
		(view: Omit<RecentView, 'ts'>) => {
			setActivity(addRecentView(userId, view));
		},
		[userId]
	);

	const clearSearches = useCallback(() => {
		setActivity(clearRecentSearches(userId));
	}, [userId]);

	const clearViews = useCallback(() => {
		setActivity(clearRecentViews(userId));
	}, [userId]);

	return {
		activity,
		trackSearch,
		trackView,
		clearSearches,
		clearViews,
	};
}


